import { useImageUrl } from "@/hooks/useImage";
import { isImageId } from "@/utils/imageUtils";
import { useLingui } from "@lingui/react";

interface ActivityPicturesProps {
  pictureUrls: string[];
}

/**
 * Single Picture Item Component
 *
 * Handles resolving image IDs to URLs and displays the image
 */
function PictureItem({ urlOrId, index }: { urlOrId: string; index: number }) {
  const { i18n } = useLingui();
  const needsResolution = isImageId(urlOrId);
  const resolvedUrl = useImageUrl(needsResolution ? urlOrId : null);
  const finalUrl = needsResolution ? resolvedUrl : urlOrId;

  if (!finalUrl) {
    // Show placeholder while loading
    return (
      <div className="relative aspect-square rounded-lg overflow-hidden bg-muted animate-pulse" />
    );
  }

  return (
    <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
      <img
        src={finalUrl}
        alt={i18n._(
          { id: "activities.pictures.alt", message: "Activity picture {number}" },
          { number: index + 1 }
        )}
        loading="lazy"
        className="object-cover w-full h-full hover:scale-105 transition-transform duration-200"
      />
    </div>
  );
}

/**
 * Activity Pictures Component
 *
 * Displays a grid of pictures for accident/picture activity types
 * Supports both direct URLs and image IDs (UUIDs from the upload system)
 */
export function ActivityPictures({ pictureUrls }: ActivityPicturesProps) {
  if (!pictureUrls || pictureUrls.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {pictureUrls.map((urlOrId, index) => (
        <PictureItem key={urlOrId || index} urlOrId={urlOrId} index={index} />
      ))}
    </div>
  );
}
